/**
 * ReceiptLinesTable Component
 *
 * Table displaying receipt line items with variance handling.
 */

import { AlertTriangle, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Input,
  Select,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableFooter,
} from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import { VARIANCE_REASON_LABELS } from '@bstock/shared';
import type { SupplierCatalogItemWithDetails } from '@bstock/shared';
import type { FormLineItem, FormError, FormTotals } from '../../types';
import { ProductSearchAutocomplete } from '@/features/purchase-orders/components/ProductSearchAutocomplete';

/**
 * Build variance reason options for select
 */
const varianceReasonOptions = Object.entries(VARIANCE_REASON_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  })
);

interface ReceiptLinesTableProps {
  items: FormLineItem[];
  isFromOrder: boolean;
  currency: string;
  totals: FormTotals;
  errors: FormError[];
  supplierId?: string;
  onQuantityChange: (index: number, value: number) => void;
  onVarianceReasonChange: (index: number, value: string) => void;
  onRemoveItem: (index: number) => void;
  onAddItem?: (item: SupplierCatalogItemWithDetails, variants: SupplierCatalogItemWithDetails[]) => void;
  onPackagingChange?: (index: number, catalogItemId: string) => void;
}

export function ReceiptLinesTable({
  items,
  isFromOrder,
  currency,
  totals,
  errors,
  supplierId,
  onQuantityChange,
  onVarianceReasonChange,
  onRemoveItem,
  onAddItem,
  onPackagingChange,
}: ReceiptLinesTableProps) {
  return (
    <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-dark-500">
          Lignes de reception
        </h3>
        {totals.itemsWithVariance > 0 && (
          <Badge variant="warning">
            {totals.itemsWithVariance} ecart{totals.itemsWithVariance > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Product search for free receipt */}
      {!isFromOrder && onAddItem && (
        <div className="mb-4">
          <ProductSearchAutocomplete
            supplierId={supplierId || null}
            onSelect={onAddItem}
            placeholder="Rechercher un produit a ajouter..."
          />
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-12 text-neutral-500">
          {isFromOrder ? (
            'Aucune ligne a recevoir'
          ) : (
            'Selectionnez un fournisseur pour ajouter des produits'
          )}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Produit</TableHead>
              <TableHead className="w-[80px]">Condit.</TableHead>
              {isFromOrder && (
                <>
                  <TableHead className="w-[50px] text-right">Cmd</TableHead>
                  <TableHead className="w-[50px] text-right">Recu</TableHead>
                  <TableHead className="w-[50px] text-right">Reste</TableHead>
                </>
              )}
              <TableHead className="w-[80px] text-right">Qte</TableHead>
              {isFromOrder && (
                <TableHead className="w-[60px] text-right">Ecart</TableHead>
              )}
              <TableHead className="w-[80px] text-right">PU HT</TableHead>
              <TableHead className="w-[90px] text-right">Total HT</TableHead>
              {isFromOrder && (
                <TableHead className="w-[140px]">Motif ecart</TableHead>
              )}
              {!isFromOrder && (
                <TableHead className="w-[50px]"></TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, index) => {
              // Variance is calculated against quantityRemaining (what we expect for THIS receipt)
              const variance =
                item.quantityRemaining !== null
                  ? item.quantityReceived - item.quantityRemaining
                  : null;
              const hasVariance = variance !== null && variance !== 0;
              const totalLine = item.quantityReceived * item.unitPriceExclTax;

              return (
                <TableRow key={item.rawProductId + index}>
                  <TableCell>
                    <div>
                      <span className="font-medium">{item.rawProductName}</span>
                      <span className="block text-xs text-neutral-500">
                        {item.rawProductSku}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-neutral-600 text-sm">
                    {(item.packagingOptions?.length ?? 0) > 1 && onPackagingChange ? (
                      <Select
                        value={item.catalogItemId ?? ''}
                        onChange={(value) => onPackagingChange(index, value)}
                        options={item.packagingOptions!.map((p) => ({
                          value: p.catalogItemId,
                          label: `${p.packagingName} (${p.conversionToRecipeUnit} ${item.recipeUnitSymbol})`,
                        }))}
                        className="text-sm"
                      />
                    ) : (
                      item.packagingName
                    )}
                  </TableCell>
                  {isFromOrder && (
                    <>
                      <TableCell className="text-right text-neutral-500">
                        {item.quantityOrdered}
                      </TableCell>
                      <TableCell className="text-right text-neutral-500">
                        {item.quantityAlreadyReceived ?? 0}
                      </TableCell>
                      <TableCell className="text-right font-medium text-primary-600">
                        {item.quantityRemaining}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min={0}
                      max={Math.min(item.quantityRemaining ?? 1000, 1000)}
                      value={item.quantityReceived}
                      onChange={(e) => {
                        const maxQty = Math.min(item.quantityRemaining ?? 1000, 1000);
                        onQuantityChange(index, Math.min(Math.max(0, parseInt(e.target.value) || 0), maxQty));
                      }}
                      className="w-16 text-right"
                    />
                  </TableCell>
                  {isFromOrder && (
                    <TableCell className="text-right">
                      {variance !== null && (
                        <span
                          className={
                            variance < 0
                              ? 'text-danger-600 font-medium'
                              : variance > 0
                              ? 'text-warning-600 font-medium'
                              : 'text-success-600'
                          }
                        >
                          {variance === 0 ? '✓' : variance > 0 ? `+${variance}` : variance}
                        </span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right text-neutral-600">
                    {formatCentimes(item.unitPriceExclTax, currency)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCentimes(totalLine, currency)}
                  </TableCell>
                  {isFromOrder && (
                    <TableCell>
                      {hasVariance ? (
                        <Select
                          value={item.varianceReason ?? ''}
                          onChange={(value) =>
                            onVarianceReasonChange(index, value)
                          }
                          options={varianceReasonOptions}
                          placeholder="Selection"
                          error={
                            errors.some(
                              (err) => err.field === `variance-${item.rawProductId}`
                            )
                              ? ' '
                              : undefined
                          }
                        />
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </TableCell>
                  )}
                  {!isFromOrder && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveItem(index)}
                      >
                        <Trash2 className="w-4 h-4 text-danger-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={isFromOrder ? 8 : 4}
                className="text-right font-semibold"
              >
                Total HT
              </TableCell>
              <TableCell className="text-right font-semibold text-lg">
                {formatCentimes(totals.totalHT, currency)}
              </TableCell>
              {isFromOrder && <TableCell />}
              {!isFromOrder && <TableCell />}
            </TableRow>
          </TableFooter>
        </Table>
      )}

      {/* Variance warning */}
      {isFromOrder && totals.itemsWithVariance > 0 && (
        <div className="mt-4 bg-warning-50 border border-warning-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-warning-800">
                {totals.itemsWithVariance} ligne{totals.itemsWithVariance > 1 ? 's' : ''} avec ecart
              </h3>
              <p className="text-sm text-warning-700 mt-1">
                Veuillez renseigner un motif pour chaque ecart avant de valider la reception.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
